/**
 * Vista de datos: tabla o tarjetas
 * -----------------------------------------------------------------------------
 * ⚠ LOS LISTADOS DE LA SUITE VAN CONTRA EL SERVIDOR. Este es el uso normal, y
 * el que hay que copiar:
 *
 *   <div data-company-data-view
 *        data-company-data-view-id="lotes"
 *        data-company-data-view-url="/lotes/datos"
 *        data-company-data-view-total="{{ paginator.totalItemCount }}"
 *        data-company-data-view-page="1">
 *     <table class="table">
 *       <thead>
 *         <tr>
 *           <th data-card-title data-sort-field="nombre">Lote</th>
 *           <th data-sort-field="variedad">Variedad</th>
 *           <th data-card-hide>ID</th>
 *           <th data-card-actions data-card-no-sort>Acciones</th>
 *         </tr>
 *       </thead>
 *       <tbody>{{ include('lotes/_filas.html.twig') }}</tbody>
 *     </table>
 *   </div>
 *
 * Sin `url`, el listado reparte en el navegador las filas que ya están en el
 * HTML. Eso parece funcionar en desarrollo con veinte filas de prueba y en
 * producción manda 40.000 al navegador: la respuesta pesa, el buscador solo
 * encuentra lo que ya viajó y el recuento del pie miente. El componente avisa
 * por consola cuando pasa.
 *
 * El modo cliente existe para catálogos cortos y fijos —una lista de estados,
 * los tipos de documento—, y hay que pedirlo a propósito con
 * `data-company-data-view-local`.
 *
 * La tabla es la ÚNICA fuente: las tarjetas se derivan de ella. Quien produzca
 * la tabla —Twig, un render en JS, lo que sea— no tiene que saber que existe la
 * vista de tarjetas, y no hay dos caminos de render que mantener sincronizados.
 *
 * Diferencias con el original de `table-rowAsinc`, del que se toma el diseño:
 *
 *   · Se cambia de vista EN SITIO. El original hacía `location.reload()`.
 *   · La preferencia va en `localStorage`, no en una cookie por URL: no tiene
 *     por qué viajar al servidor en cada petición.
 *   · La preferencia se guarda por `data-company-data-view-id`, así dos tablas
 *     en la misma página no se pisan (la cookie era una sola por ruta).
 *   · Un MutationObserver mantiene las tarjetas al día cuando la tabla se
 *     repinta sola (paginación, websocket…), sin que el renderizador avise.
 *
 * Hay tres vistas: `table`, `cards` y `board`. La de tablero agrupa las
 * tarjetas en columnas con encabezado —«faltantes por despachar», «pedidos por
 * facturar»— y solo aparece si alguna columna está marcada como agrupadora.
 *
 * Configuración por columna, en el `<th>`:
 *   data-card-title       ese valor es el título de la tarjeta
 *   data-card-hide        no aparece en la tarjeta
 *   data-card-actions     su contenido va a la zona de acciones
 *   data-card-group       agrupa por esta columna (habilita la vista tablero)
 *   data-card-group-order lista fija de grupos, separada por comas: respeta ese
 *                         orden y muestra los vacíos
 *
 * Configuración del contenedor:
 *   data-company-data-view="cards"          vista inicial si no hay preferencia
 *   data-company-data-view-id="lotes"       clave con la que se recuerda
 *   data-company-data-view-groups="2"       columnas de pares dentro de la tarjeta
 *   data-company-data-view-columns="3"      tarjetas por fila en la vista tarjetas
 *   data-company-data-view-board-columns="3" columnas de la vista tablero
 *   data-company-data-view-no-switch        no dibuja el selector
 */

import { dataViewES } from './locale-es.js';
import { initMdbOn } from './mdb-init.js';
import { normalizeText } from './normalize-text.js';
import { GAP, MAX_PAGES, pageNumbers } from './pager.js';

const STORAGE_PREFIX = 'company-theme:data-view:';

// Tamaños de página. El primero es el que se usa si no se declara otro: siete
// filas caben en una pantalla sin obligar a bajar, que es lo que se quiere de un
// listado que se consulta de un vistazo.
const PAGE_SIZES = [7, 25, 50, 100];


function readPreference(id) {
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + id);
  } catch (error) {
    // Modo privado o almacenamiento bloqueado: se sigue sin recordar.
    return null;
  }
}

function writePreference(id, view) {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + id, view);
  } catch (error) {
    /* no se puede recordar; la vista sigue funcionando */
  }
}

class DataView {
  constructor(root, components = {}) {
    this.root = root;
    // Componentes de MDB, para el desplegable del tamaño de página: el paginador
    // se genera en cada repintado y hay que inicializarlo a mano.
    this.mdb = components;
    this.table = root.querySelector('table');
    this.id =
      root.getAttribute('data-company-data-view-id') ||
      (this.table && this.table.id) ||
      'default';
    this.groups = Number(root.getAttribute('data-company-data-view-groups')) || 2;
    this.columns = Number(root.getAttribute('data-company-data-view-columns')) || 1;
    this.boardColumns = Number(root.getAttribute('data-company-data-view-board-columns')) || 3;

    this.hasBoard = !!this.table.querySelector('thead th[data-card-group]');

    // Paginación del lado del cliente. Va puesta de serie: un listado sin pie
    // no dice cuántos registros hay, y «se ven cinco» no distingue entre cinco
    // y quinientos. Se apaga con `page-size="0"` cuando pagina el servidor, para
    // no montar un paginador encima de otro.
    const declared = root.getAttribute('data-company-data-view-page-size');
    this.pageSize = declared === null ? PAGE_SIZES[0] : Number(declared) || 0;
    this.pageSizes = this._readPageSizes(root);
    this.maxPages = Number(root.getAttribute('data-company-data-view-max-pages')) || MAX_PAGES;
    this.page = 1;

    this.search = '';
    this.sort = { index: null, dir: 'asc' };
    // Página actual de cada columna del tablero, por nombre de grupo.
    this.boardPages = new Map();

    // Modo remoto: el theme dibuja los controles pero NO toca las filas. Manda
    // eventos y espera a que la aplicación traiga la página ya buscada y
    // ordenada. Es lo que hace falta cuando pagina el servidor: filtrar aquí
    // solo alcanzaría a las filas que ya viajaron, y el usuario creería que ha
    // buscado en todo.
    this.url = root.getAttribute('data-company-data-view-url');
    this.remote = this.url !== null || root.hasAttribute('data-company-data-view-remote');

    this._warnIfNotRemote(root);

    if (this.remote) {
      this.total = Number(root.getAttribute('data-company-data-view-total')) || 0;
      this.page = Number(root.getAttribute('data-company-data-view-page')) || 1;
      this.params = {
        query: root.getAttribute('data-company-data-view-param-query') || 'q',
        page: root.getAttribute('data-company-data-view-param-page') || 'page',
        limit: root.getAttribute('data-company-data-view-param-limit') || 'limit',
        sort: root.getAttribute('data-company-data-view-param-sort') || 'sort',
        dir: root.getAttribute('data-company-data-view-param-dir') || 'dir',
      };
      this.debounce = Number(root.getAttribute('data-company-data-view-debounce')) || 300;
      this.controller = null;
      this.timer = null;
    }

    const initial = root.getAttribute('data-company-data-view');
    const stored = readPreference(this.id);
    this.view = this._normalize(stored || initial);

    // La tabla va sobre una superficie blanca; las otras dos vistas no, porque
    // ya están hechas de tarjetas y meterlas dentro de otra caja blanca las
    // aplanaría.
    this.tableHost = this._wrapTable();

    this.cards = document.createElement('div');
    this.cards.className = 'data-cards';
    this.cards.style.setProperty('--data-cards-columns', String(this.columns));
    this.cards.hidden = true;
    this.tableHost.parentNode.insertBefore(this.cards, this.tableHost.nextSibling);

    this.board = document.createElement('div');
    this.board.className = 'data-board';
    this.board.style.setProperty('--data-board-columns', String(this.boardColumns));
    this.board.hidden = true;
    this.tableHost.parentNode.insertBefore(this.board, this.cards.nextSibling);

    // Sin registros no se muestra ninguna de las tres vistas, sino la caja de
    // estado vacío: una tabla con la cabecera y nada debajo no dice nada.
    this.emptyHost = document.createElement('div');
    this.emptyHost.hidden = true;
    this.tableHost.parentNode.insertBefore(this.emptyHost, this.board.nextSibling);

    // Fallo al consultar: no es lo mismo que «no hay datos», y confundirlos hace
    // creer al usuario que lo que busca no existe.
    this.errorHost = document.createElement('div');
    this.errorHost.hidden = true;
    this.tableHost.parentNode.insertBefore(this.errorHost, this.emptyHost.nextSibling);

    this.pager = document.createElement('nav');
    this.pager.className = 'data-pager';
    this.pager.hidden = true;
    this.tableHost.parentNode.insertBefore(this.pager, this.errorHost.nextSibling);

    this._addToolbar();
    if (!root.hasAttribute('data-company-data-view-no-switch')) this._addSwitch();

    this._observe();
    this.setView(this.view);
  }

  /**
   * Avisa si el listado no declara de dónde salen los datos.
   *
   * **Los listados de la suite van contra el servidor.** Un listado en modo
   * cliente parece funcionar en desarrollo, con veinte filas de prueba, y en
   * producción manda las 40.000 al navegador: la respuesta pesa, el buscador
   * solo encuentra lo que ya viajó y el recuento del pie miente.
   *
   * El aviso existe porque este componente se copia de un ejemplo a otro, y un
   * ejemplo sin `url` se propaga sin que nadie lo decida. Se calla declarando
   * una de las dos cosas, que es justo la decisión que hay que tomar:
   *
   *   data-company-data-view-url="/lotes/datos"   el theme consulta
   *   data-company-data-view-remote               consulta la aplicación
   *   data-company-data-view-local                a propósito en cliente
   */
  /**
   * El servidor no dijo cuántos hay. Se avisa una sola vez por listado: en una
   * tabla que se repagina sola, un aviso por consulta llena la consola y acaba
   * ignorándose.
   */
  _warnSinTotal() {
    if (this._avisadoSinTotal) return;
    this._avisadoSinTotal = true;
    if (typeof console === 'undefined') return;

    console.warn(
      `company-theme: el listado «${this.id}» no recibe el total de resultados.\n` +
        'Sin él el paginador cree que solo hay una página y el pie solo puede ' +
        'contar las filas que llegaron.\n' +
        'El endpoint tiene que devolver la cabecera «X-Total-Count», o incluir ' +
        'data-company-data-view-total en el fragmento HTML.'
    );
  }

  _warnIfNotRemote(root) {
    if (this.remote) return;
    if (root.hasAttribute('data-company-data-view-local')) return;
    if (typeof console === 'undefined') return;

    console.warn(
      `company-theme: el listado «${this.id}» no declara origen de datos y va en modo cliente.\n` +
        'Los listados de la suite consultan al servidor: añade ' +
        'data-company-data-view-url="/tu/endpoint".\n' +
        'Si de verdad son pocos registros y fijos —un catálogo corto—, decláralo con ' +
        'data-company-data-view-local y este aviso desaparece.',
      root
    );
  }

  /** Descarta vistas que no existen para esta tabla. */
  _normalize(view) {
    if (view === 'cards') return 'cards';
    if (view === 'board' && this.hasBoard) return 'board';
    return 'table';
  }

  /**
   * Barra superior: buscador a la izquierda, orden y selector de vista a la
   * derecha. Se crea aunque no haya buscador, para que el selector de vista no
   * cambie de sitio según la configuración.
   */
  _addToolbar() {
    this.toolbar = document.createElement('div');
    this.toolbar.className = 'data-toolbar';
    this.root.insertBefore(this.toolbar, this.root.firstChild);

    if (!this.root.hasAttribute('data-company-data-view-no-search')) this._addSearch();

    this.tools = document.createElement('div');
    this.tools.className = 'data-toolbar-tools';
    this.toolbar.appendChild(this.tools);

    this.sortHost = document.createElement('div');
    this.sortHost.className = 'data-sort';
    this.sortHost.hidden = true;
    this.tools.appendChild(this.sortHost);
  }

  _addSearch() {
    const box = document.createElement('div');
    box.className = 'data-search';
    box.innerHTML = `
      <i class="fas fa-magnifying-glass data-search-icon"></i>
      <input type="search" class="data-search-input" placeholder="${dataViewES.search}"
        aria-label="${dataViewES.search}" />
    `;

    this.toolbar.appendChild(box);
    this.searchInput = box.querySelector('input');

    // Sin retardo: filtra sobre filas que ya están en memoria. En modo remoto lo
    // que llega es el evento, y quien consulte decide si espera.
    this.searchInput.addEventListener('input', () => this.setSearch(this.searchInput.value));
  }

  /**
   * Desplegable de orden. Existe porque en tarjetas y columnas no hay cabeceras
   * que pulsar: la misma acción necesita dos mandos según la vista, pero el
   * estado que cambian es uno solo.
   */
  _renderSort() {
    const columnas = this.sortableColumns;

    // En la tabla se ordena pulsando la cabecera; aquí sobraría.
    this.sortHost.hidden = this.view === 'table' || !columnas.length || this.isEmpty;
    if (this.sortHost.hidden) return;

    const actual = this.sort.index === null ? null : this._columns()[this.sort.index];
    const etiqueta = actual
      ? `${actual.label} · ${this.sort.dir === 'asc' ? dataViewES.sortAsc : dataViewES.sortDesc}`
      : dataViewES.sortBy;

    const opciones = columnas
      .map((column) =>
        ['asc', 'desc']
          .map((dir) => {
            const activa = this.sort.index === column.index && this.sort.dir === dir;
            const texto = dir === 'asc' ? dataViewES.sortAsc : dataViewES.sortDesc;
            return `<li><button type="button" class="dropdown-item${activa ? ' active' : ''}"
              data-sort-index="${column.index}" data-sort-dir="${dir}">
              <i class="fas fa-arrow-${dir === 'asc' ? 'down' : 'up'}-a-z me-2"></i>${column.label} · ${texto}
            </button></li>`;
          })
          .join('')
      )
      .join('');

    this.sortHost.innerHTML = `
      <div class="dropdown">
        <button type="button" class="data-sort-toggle" data-mdb-dropdown-init
          data-mdb-toggle="dropdown" aria-expanded="false">
          <i class="fas fa-arrow-down-short-wide me-2"></i>${etiqueta}
        </button>
        <ul class="dropdown-menu">${opciones}</ul>
      </div>
    `;

    this.sortHost.querySelectorAll('button[data-sort-index]').forEach((button) => {
      button.addEventListener('click', () =>
        this.sortBy(Number(button.dataset.sortIndex), button.dataset.sortDir)
      );
    });

    initMdbOn(this.sortHost, this.mdb);
  }

  /** Cabeceras pulsables de la tabla, con la flecha del orden en uso. */
  _renderSortableHeaders() {
    const headers = this.table.querySelectorAll('thead th');
    const columns = this._columns();

    Array.prototype.forEach.call(headers, (th, index) => {
      const column = columns[index];
      const ordenable = column && !column.actions && !column.hidden && column.sortable !== false;

      th.classList.toggle('data-sortable', ordenable);
      if (!ordenable) return;

      const activa = this.sort.index === index;
      th.classList.toggle('data-sorted', activa);
      th.setAttribute('aria-sort', activa ? (this.sort.dir === 'asc' ? 'ascending' : 'descending') : 'none');

      if (!th.querySelector('.data-sort-arrow')) {
        const arrow = document.createElement('i');
        arrow.className = 'fas data-sort-arrow';
        th.appendChild(arrow);
      }

      const arrow = th.querySelector('.data-sort-arrow');
      arrow.classList.remove('fa-arrow-up', 'fa-arrow-down', 'fa-arrows-up-down');
      arrow.classList.add(
        !activa ? 'fa-arrows-up-down' : this.sort.dir === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'
      );

      // El manejador se guarda en el propio `<th>` para poder retirarlo en
      // `dispose()`. Sin eso, volver a montar la vista sobre la misma tabla deja
      // el de la instancia anterior enganchado, y un clic ejecuta los dos.
      if (!th.__companySort) {
        th.__companySort = () => this.sortBy(index);
        th.addEventListener('click', th.__companySort);
      }
    });
  }

  _addSwitch() {
    const existing = this.root.querySelector('[data-company-data-view-switch]');
    this.switch = existing || document.createElement('div');

    if (!existing) {
      this.switch.className = 'view-switch';
      (this.tools || this.root).appendChild(this.switch);
    }

    const board = this.hasBoard
      ? `<button type="button" class="btn btn-sm" data-view="board"
          aria-pressed="false" title="${dataViewES.board}">
          <i class="fas fa-table-columns"></i><span>${dataViewES.board}</span>
        </button>`
      : '';

    this.switch.innerHTML = `
      <button type="button" class="btn btn-sm" data-view="table"
        aria-pressed="false" title="${dataViewES.table}">
        <i class="fas fa-table-list"></i><span>${dataViewES.table}</span>
      </button>
      <button type="button" class="btn btn-sm" data-view="cards"
        aria-pressed="false" title="${dataViewES.cards}">
        <i class="fas fa-table-cells-large"></i><span>${dataViewES.cards}</span>
      </button>
      ${board}
    `;

    this.switch.querySelectorAll('button[data-view]').forEach((button) => {
      button.addEventListener('click', () => this.setView(button.dataset.view));
    });
  }

  /**
   * Repinta las tarjetas cuando la tabla cambia por su cuenta: paginación,
   * respuesta de websocket, filtro… Sin esto habría que avisar a mano desde el
   * código que repinta la tabla, que es justo el acoplamiento que se evita.
   */
  _observe() {
    const body = this.table.tBodies[0];
    if (!body || typeof MutationObserver === 'undefined') return;

    // Repinta, pero NO vuelve a guardar la preferencia ni reemite el evento:
    // esto se dispara con cada cambio de la tabla, no es una elección del
    // usuario. Se reevalúa todo porque la tabla puede haberse quedado sin filas,
    // o haber recuperado alguna.
    this.observer = new MutationObserver(() => {
      // Filas nuevas llegan sin ordenar. Se recolocan antes de repintar, para
      // que una tabla que se recarga por AJAX no pierda el orden elegido.
      if (this.sort.index !== null && !this.remote) this._reorder();
      this._apply();
    });
    this._observeBody();
  }

  _observeBody() {
    const body = this.table.tBodies[0];
    if (!body || !this.observer) return;

    this.observer.observe(body, { childList: true, subtree: true, characterData: true });
  }

  /**
   * Envuelve la tabla en una tarjeta, salvo que ya esté dentro de una. Sin esa
   * comprobación, una tabla puesta en un `.card` del proyecto acabaría con dos
   * fondos blancos, uno dentro del otro.
   *
   * Opt-out: `data-company-data-view-no-panel`.
   *
   * @returns {HTMLElement} El elemento a mostrar u ocultar en la vista tabla.
   */
  _wrapTable() {
    if (this.root.hasAttribute('data-company-data-view-no-panel')) return this.table;
    if (this.table.closest('.card-body, .card, .page-panel')) return this.table;

    const wrap = document.createElement('div');
    wrap.className = 'data-table-panel';

    this.table.parentNode.insertBefore(wrap, this.table);
    wrap.appendChild(this.table);

    return wrap;
  }

  /** Lee la configuración de las columnas desde el `<thead>`. */
  _columns() {
    const headers = this.table.querySelectorAll('thead th');

    return Array.prototype.map.call(headers, (th, index) => ({
      index,
      label: th.textContent.trim(),
      hidden: th.hasAttribute('data-card-hide'),
      title: th.hasAttribute('data-card-title'),
      actions: th.hasAttribute('data-card-actions'),
      group: th.hasAttribute('data-card-group'),
      order: th.getAttribute('data-card-group-order'),
      sortable: !th.hasAttribute('data-card-no-sort'),
      // Nombre del campo en el backend. Con él, el servidor no tiene que
      // traducir posiciones de columna a nombres de propiedad.
      field: th.getAttribute('data-sort-field'),
    }));
  }

  /**
   * Reparte las filas por el valor de la columna agrupadora.
   *
   * El orden de los grupos es el de aparición en la tabla, salvo que el `<th>`
   * declare `data-card-group-order`: esa lista manda, y sus grupos se muestran
   * aunque estén vacíos —en un flujo de trabajo importa ver la columna vacía—.
   */
  _grouped(columns) {
    const index = columns.findIndex((column) => column.group);
    if (index === -1) return null;

    const declared = columns[index].order
      ? columns[index].order.split(',').map((name) => name.trim()).filter(Boolean)
      : [];

    const groups = new Map();
    declared.forEach((name) => groups.set(name, []));

    // Las filtradas, no todas: si no, buscar no tendría efecto en el tablero.
    const rows = this.filteredRows;

    Array.prototype.forEach.call(rows, (row) => {
      const cell = row.cells[index];
      const key = cell ? cell.textContent.trim() : '';
      const name = key || dataViewES.ungrouped;

      if (!groups.has(name)) groups.set(name, []);
      groups.get(name).push(row);
    });

    return { index, groups };
  }

  render() {
    const columns = this._columns();
    // Solo la página actual: a diferencia de la tabla, aquí las tarjetas se
    // generan de cero, así que no se pinta lo que no se va a ver.
    const rows = this.pageRows;

    this.cards.innerHTML = '';

    if (!rows.length) {
      this.cards.appendChild(this._empty());
      return;
    }

    rows.forEach((row) => {
      this.cards.appendChild(this._card(row, columns));
    });
  }

  renderBoard() {
    const columns = this._columns();
    const grouped = this._grouped(columns);

    this.board.innerHTML = '';
    if (!grouped) return;

    // La columna agrupadora ya es el encabezado: repetirla en cada tarjeta
    // sería ruido.
    const cardColumns = columns.map((column, index) =>
      index === grouped.index ? { ...column, hidden: true } : column
    );

    grouped.groups.forEach((rows, name) => {
      const column = document.createElement('section');
      column.className = 'data-board-column';

      const header = document.createElement('div');
      header.className = 'data-board-header';
      header.innerHTML = `<h3 class="data-board-title"></h3><span class="data-board-count"></span>`;
      header.querySelector('.data-board-title').textContent = name;
      // El total del grupo, no el de la página: la cuenta de la columna es lo
      // que se mira para saber cuánto trabajo hay pendiente ahí.
      header.querySelector('.data-board-count').textContent = String(rows.length);
      column.appendChild(header);

      // Cada columna pagina por su cuenta. Un mismo número de página para todas
      // no sirve: tienen cantidades muy distintas, y avanzar en una que ya se
      // acabó dejaría huecos vacíos al lado de otra con cien pendientes.
      const pages = this.pageSize ? Math.max(1, Math.ceil(rows.length / this.pageSize)) : 1;
      const page = Math.min(this.boardPages.get(name) || 1, pages);
      const visibles = this.pageSize
        ? rows.slice((page - 1) * this.pageSize, page * this.pageSize)
        : rows;

      if (!rows.length) {
        const empty = document.createElement('p');
        empty.className = 'data-board-empty';
        empty.textContent = dataViewES.emptyGroup;
        column.appendChild(empty);
      } else {
        visibles.forEach((row) => column.appendChild(this._card(row, cardColumns)));
      }

      if (pages > 1) column.appendChild(this._boardPager(name, page, pages));

      this.board.appendChild(column);
    });
  }

  /** Pie de una columna del tablero: anterior, «X de Y», siguiente. */
  _boardPager(name, page, pages) {
    const nav = document.createElement('nav');
    nav.className = 'data-board-pager';
    nav.innerHTML = `
      <button type="button" class="data-pager-nav" data-board-page="${page - 1}"
        ${page === 1 ? 'disabled' : ''} aria-label="${dataViewES.previous}">
        <i class="fas fa-chevron-left"></i>
      </button>
      <span class="data-board-pager-range">${dataViewES.boardRange
        .replace('{pagina}', page)
        .replace('{paginas}', pages)}</span>
      <button type="button" class="data-pager-nav" data-board-page="${page + 1}"
        ${page === pages ? 'disabled' : ''} aria-label="${dataViewES.next}">
        <i class="fas fa-chevron-right"></i>
      </button>
    `;

    nav.querySelectorAll('button[data-board-page]').forEach((button) => {
      button.addEventListener('click', () =>
        this.goToBoardPage(name, Number(button.dataset.boardPage))
      );
    });

    return nav;
  }

  /**
   * @param {string} group Nombre de la columna.
   * @param {number} page
   */
  goToBoardPage(group, page) {
    const target = Math.max(1, page);
    if ((this.boardPages.get(group) || 1) === target) return;

    this.boardPages.set(group, target);
    if (!this.remote) this.renderBoard();

    this.root.dispatchEvent(
      new CustomEvent('boardPageChanged.company.dataview', {
        bubbles: true,
        detail: { group, page: target, pageSize: this.pageSize, id: this.id },
      })
    );
  }

  /**
   * Estado vacío. El theme pone la caja; el texto y el botón los pone la app
   * con un `<template data-company-data-view-empty>` dentro del contenedor:
   *
   *   <template data-company-data-view-empty>
   *     <i class="fas fa-seedling data-view-empty-icon"></i>
   *     <p class="data-view-empty-title">Todavía no hay cultivos</p>
   *     <p class="data-view-empty-text">Creá el primero para empezar.</p>
   *     <div class="data-view-empty-actions">
   *       <a href="/cultivos/nuevo" class="btn btn-primary">Crear cultivo nuevo</a>
   *     </div>
   *   </template>
   */
  _empty() {
    const box = document.createElement('div');
    box.className = 'data-view-empty';

    const template = this.root.querySelector('[data-company-data-view-empty]');

    if (template) {
      box.appendChild(template.content ? template.content.cloneNode(true) : template.cloneNode(true));
    } else {
      const text = document.createElement('p');
      text.className = 'data-view-empty-text';
      text.textContent = dataViewES.empty;
      box.appendChild(text);
    }

    return box;
  }

  _card(row, columns) {
    const card = document.createElement('article');
    card.className = 'data-card';

    const pairs = [];
    let title = null;
    let actions = null;

    Array.prototype.forEach.call(row.cells, (cell, index) => {
      const column = columns[index];
      if (!column || column.hidden) return;

      if (column.title) {
        title = cell.textContent.trim();
        return;
      }

      if (column.actions) {
        actions = cell;
        return;
      }

      pairs.push({ label: column.label, cell });
    });

    if (title) {
      const header = document.createElement('div');
      header.className = 'data-card-header';
      header.innerHTML = `<h3 class="data-card-title"></h3>`;
      header.querySelector('.data-card-title').textContent = title;
      card.appendChild(header);
    }

    const body = document.createElement('div');
    body.className = 'data-card-body';
    body.style.setProperty('--data-card-groups', String(this.groups));

    // Los pares se reparten en columnas equilibradas, no de a uno por columna.
    const perGroup = Math.ceil(pairs.length / this.groups) || 1;

    for (let start = 0; start < pairs.length; start += perGroup) {
      const grid = document.createElement('div');
      grid.className = 'data-card-grid';

      pairs.slice(start, start + perGroup).forEach(({ label, cell }) => {
        const dt = document.createElement('span');
        dt.className = 'data-card-label';
        dt.textContent = label;

        const dd = document.createElement('span');
        dd.className = 'data-card-value';
        // Se clona el contenido, no el texto: así sobreviven los badges, los
        // enlaces y las barras de progreso que ya trae la celda.
        Array.prototype.forEach.call(cell.childNodes, (node) => {
          dd.appendChild(node.cloneNode(true));
        });

        grid.appendChild(dt);
        grid.appendChild(dd);
      });

      body.appendChild(grid);
    }

    card.appendChild(body);

    if (actions) {
      const box = document.createElement('div');
      box.className = 'data-card-actions';
      Array.prototype.forEach.call(actions.childNodes, (node) => {
        box.appendChild(node.cloneNode(true));
      });
      card.appendChild(box);
    }

    return card;
  }

  /**
   * @param {'table'|'cards'} view
   */
  get isEmpty() {
    const body = this.table.tBodies[0];
    return !body || body.rows.length === 0;
  }

  /** Todas las filas del `tbody`, sin filtrar ni paginar. */
  get allRows() {
    const body = this.table.tBodies[0];
    return body ? Array.prototype.slice.call(body.rows) : [];
  }

  /**
   * Las filas que pasan la búsqueda. Es la base de todo lo demás: el recuento,
   * las páginas, las tarjetas y las columnas cuentan sobre esto, no sobre el
   * total, para que el pie no diga «10 resultados» con tres en pantalla.
   */
  get filteredRows() {
    const rows = this.allRows;
    if (!this.search || this.remote) return rows;

    const term = normalizeText(this.search);
    return rows.filter((row) => normalizeText(row.textContent).includes(term));
  }

  /** Hay filas, pero ninguna coincide con lo buscado. */
  get isFiltered() {
    if (!this.search) return false;

    // En remoto las filas que no coinciden ni siquiera viajan, así que un
    // listado vacío CON búsqueda puesta es «no encontré eso», no «todavía no
    // hay nada»: enseñar aquí el «crea el primero» sería engañoso, porque
    // registros hay, solo que ninguno con ese texto.
    if (this.remote) return this.isEmpty;

    return this.allRows.length > 0 && this.filteredRows.length === 0;
  }

  get totalRows() {
    return this.remote ? this.total : this.filteredRows.length;
  }

  get totalPages() {
    if (!this.pageSize) return 1;
    return Math.max(1, Math.ceil(this.totalRows / this.pageSize));
  }

  /** Las filas que corresponden a la página actual. */
  get pageRows() {
    const rows = this.filteredRows;
    // En remoto la página ya viene servida: repartirla otra vez la partiría.
    if (!this.pageSize || this.remote) return rows;

    const start = (this.page - 1) * this.pageSize;
    return rows.slice(start, start + this.pageSize);
  }

  // ---------------------------------------------------------------------------
  // Búsqueda
  // ---------------------------------------------------------------------------

  /**
   * @param {string} text
   */
  setSearch(text) {
    const value = String(text || '');
    if (value === this.search) return;

    this.search = value;
    this.page = 1; // lo buscado casi nunca tiene tantas páginas como el total
    this.boardPages.clear();

    // Al teclear se espera: sin retardo, «aguacate» serían ocho consultas.
    if (this.remote) this.reload({ debounce: true });
    else this._apply();

    this.root.dispatchEvent(
      new CustomEvent('searchChanged.company.dataview', {
        bubbles: true,
        detail: { search: this.search, id: this.id },
      })
    );
  }

  // ---------------------------------------------------------------------------
  // Orden
  // ---------------------------------------------------------------------------

  /** Columnas por las que se puede ordenar. Las de acciones no cuentan. */
  get sortableColumns() {
    return this._columns().filter(
      (column) => !column.actions && !column.hidden && column.sortable !== false
    );
  }

  /**
   * @param {number} index Índice de la columna en el `<tr>`.
   * @param {'asc'|'desc'} [dir] Sin argumento, alterna.
   */
  sortBy(index, dir) {
    const column = this._columns()[index];
    if (!column || column.actions || column.sortable === false) return;

    const direction = dir || (this.sort.index === index && this.sort.dir === 'asc' ? 'desc' : 'asc');
    this.sort = { index, dir: direction };

    this.page = 1; // el orden cambia qué hay en cada página

    if (this.remote) {
      this.reload();
    } else {
      this._reorder();
      this._apply();
    }

    this.root.dispatchEvent(
      new CustomEvent('sortChanged.company.dataview', {
        bubbles: true,
        detail: { index, dir: direction, column: column.label, id: this.id },
      })
    );
  }

  /**
   * Reordena el `<tbody>`.
   *
   * Se mueve el DOM en vez de llevar una lista aparte porque la tabla es la
   * fuente de las otras dos vistas: ordenándola, las tarjetas y las columnas
   * quedan ordenadas sin una sola línea más.
   */
  _reorder() {
    const body = this.table.tBodies[0];
    if (!body || this.sort.index === null) return;

    const rows = this.allRows.slice();
    const factor = this.sort.dir === 'desc' ? -1 : 1;

    rows.sort((a, b) => factor * this._compare(a, b, this.sort.index));

    // Sin avisar al observador: es un cambio nuestro, no de la aplicación, y
    // volvería a entrar en `_apply()` mientras estamos dentro.
    this._quiet(() => rows.forEach((row) => body.appendChild(row)));
  }

  /**
   * Valor por el que se compara una celda.
   *
   * `data-sort-value` en el `<td>` manda. Hace falta para lo que se ve distinto
   * de como se ordena: una fecha «14/08/2026», un importe «1.234,50» o un estado
   * pintado como etiqueta de color se ordenarían por su texto, que no es su
   * orden real.
   */
  _sortValue(row, index) {
    const cell = row.cells[index];
    if (!cell) return '';

    const explicit = cell.getAttribute('data-sort-value');
    return explicit !== null ? explicit : cell.textContent.trim();
  }

  _compare(a, b, index) {
    const va = this._sortValue(a, index);
    const vb = this._sortValue(b, index);

    const numero = /^-?\d+(?:[.,]\d+)?$/;
    if (numero.test(va) && numero.test(vb)) {
      return Number(va.replace(',', '.')) - Number(vb.replace(',', '.'));
    }

    // Sin tildes y sin mayúsculas, igual que el resto de búsquedas del theme.
    return normalizeText(va).localeCompare(normalizeText(vb));
  }

  /** Ejecuta un cambio del DOM sin que el observador lo tome por externo. */
  _quiet(fn) {
    if (!this.observer) {
      fn();
      return;
    }

    this.observer.disconnect();
    fn();
    this.observer.takeRecords();
    this._observeBody();
  }

  /**
   * @param {number} page
   */
  goToPage(page) {
    const target = Math.min(Math.max(1, page), this.totalPages);
    if (target === this.page) return;

    // En remoto no se toca nada: la página la trae la aplicación. Cambiarla aquí
    // repartiría las filas que ya están, que son justo las de la página vieja.
    if (this.remote) {
      this.page = target;
      // Con URL, el theme consulta. Sin ella, solo avisa: la aplicación tiene su
      // propia forma de traer los datos y el evento es el enganche.
      this.reload();

      this.root.dispatchEvent(
        new CustomEvent('pageChanged.company.dataview', {
          bubbles: true,
          detail: { page: target, pages: this.totalPages, id: this.id },
        })
      );

      this._renderPager();
      return;
    }

    this.page = target;
    this._apply();

    this.root.dispatchEvent(
      new CustomEvent('pageChanged.company.dataview', {
        bubbles: true,
        detail: { page: this.page, pages: this.totalPages, id: this.id },
      })
    );
  }

  /**
   * En la vista de tabla las filas sobrantes se ocultan en su sitio; en las
   * otras dos se pinta solo la página, porque las tarjetas se generan de cero.
   */
  _paginateTable() {
    if (!this.pageSize) return;

    const visibles = this.pageRows;
    this.allRows.forEach((row) => {
      row.hidden = visibles.indexOf(row) === -1;
    });
  }

  /** Los tamaños ofrecidos, con el que esté en uso incluido aunque sea otro. */
  _readPageSizes(root) {
    const declared = root.getAttribute('data-company-data-view-page-sizes');

    const sizes = declared
      ? declared
          .split(',')
          .map((value) => Number(value.trim()))
          .filter((value) => value > 0)
      : PAGE_SIZES.slice();

    // Si la app fijó un tamaño que no está en la lista, se añade: si no, el
    // selector mostraría un valor que no puede volver a elegirse.
    if (this.pageSize && sizes.indexOf(this.pageSize) === -1) sizes.push(this.pageSize);

    return sizes.sort((a, b) => a - b);
  }

  /**
   * @param {number} size Filas por página.
   */
  setPageSize(size) {
    const value = Number(size);
    if (!value || value === this.pageSize) return;

    // Se vuelve a la primera página: mantener el número dejaría al usuario en
    // una página que con el tamaño nuevo puede que ni exista.
    this.pageSize = value;
    this.page = 1;
    this.boardPages.clear();

    if (this.remote) {
      this.reload();
      this._renderPager();
    } else {
      this._apply();
    }

    this.root.dispatchEvent(
      new CustomEvent('pageSizeChanged.company.dataview', {
        bubbles: true,
        detail: { pageSize: this.pageSize, pages: this.totalPages, id: this.id },
      })
    );
  }

  /**
   * Qué números de página pintar.
   *
   * Un listado de 4.000 registros son 600 páginas: pintarlas todas llena la
   * pantalla de botones y deja el listado fuera de la vista. Se muestra una
   * ventana alrededor de la actual, y **siempre la primera y la última**, con
   * puntos suspensivos en los huecos. Sin las de los extremos —como en el
   * original de agro, que solo centra cinco— llegar al final desde la página 1
   * costaría ciento veinte clics.
   *
   * @param {number} pages Total de páginas.
   * @returns {Array<number|symbol>} Números, con `GAP` donde va el hueco.
   */
  /** Ventana de páginas. La lógica vive en , compartida. */
  _pageNumbers(pages) {
    return pageNumbers(this.page, pages, this.maxPages);
  }
  _renderPager() {
    const pages = this.totalPages;
    // Las que pasan la búsqueda, no todas: con un filtro puesto, el recuento
    // que importa es el de lo encontrado.
    const total = this.totalRows;

    // El pie se muestra siempre que haya datos, aunque quepan en una página: el
    // recuento es información por sí mismo, y el selector de tamaño tiene que
    // seguir alcanzable para poder volver a subirlo.
    this.pager.hidden = !this.pageSize || this.isEmpty || this.isFiltered;
    if (this.pager.hidden) return;

    // En el tablero pagina cada columna, así que aquí no hay «página X de Y»:
    // solo el recuento y el tamaño, que ahí significa «cuántas por columna».
    const tablero = this.view === 'board';

    let resumen;
    if (total === 1) {
      resumen = tablero ? dataViewES.boardSummaryOne : dataViewES.summaryOne;
    } else if (tablero) {
      resumen = dataViewES.boardSummary.replace('{total}', total);
    } else {
      resumen = dataViewES.summary
        .replace('{pagina}', this.page)
        .replace('{paginas}', pages)
        .replace('{total}', total);
    }

    const plantilla = tablero ? dataViewES.perPageColumn : dataViewES.perPage;

    const opciones = this.pageSizes
      .map(
        (size) =>
          `<li><button type="button" class="dropdown-item${size === this.pageSize ? ' active' : ''}"
             data-page-size="${size}">${dataViewES.perPageOption.replace('{n}', size)}</button></li>`
      )
      .join('');

    // Los botones de página solo aparecen si hay más de una, y nunca en tablero.
    const botones = [];
    if (pages > 1 && !tablero) {
      this._pageNumbers(pages).forEach((n) => {
        if (n === GAP) {
          botones.push(`<span class="data-pager-gap" aria-hidden="true">…</span>`);
          return;
        }

        botones.push(
          `<button type="button" class="data-pager-page${n === this.page ? ' active' : ''}"
             data-page="${n}" aria-current="${n === this.page ? 'page' : 'false'}">${n}</button>`
        );
      });
    }

    this.pager.innerHTML = `
      <div class="data-pager-summary">
        <span class="data-pager-range">${resumen}</span>
        <div class="dropdown data-pager-size">
          <button type="button" class="data-pager-size-toggle" data-mdb-dropdown-init
            data-mdb-toggle="dropdown" aria-expanded="false">
            ${plantilla.replace('{n}', this.pageSize)}
            <i class="fas fa-caret-down ms-1"></i>
          </button>
          <ul class="dropdown-menu">${opciones}</ul>
        </div>
      </div>
      ${
        pages > 1 && !tablero
          ? `<div class="data-pager-controls">
        <button type="button" class="data-pager-nav" data-page="${this.page - 1}"
          ${this.page === 1 ? 'disabled' : ''} aria-label="${dataViewES.previous}">
          <i class="fas fa-chevron-left"></i>
        </button>
        ${botones.join('')}
        <button type="button" class="data-pager-nav" data-page="${this.page + 1}"
          ${this.page === pages ? 'disabled' : ''} aria-label="${dataViewES.next}">
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>`
          : ''
      }
    `;

    this.pager.querySelectorAll('button[data-page]').forEach((button) => {
      button.addEventListener('click', () => this.goToPage(Number(button.dataset.page)));
    });

    this.pager.querySelectorAll('button[data-page-size]').forEach((button) => {
      button.addEventListener('click', () => this.setPageSize(Number(button.dataset.pageSize)));
    });

    // El menú es markup recién creado: sin esto no se abre al pulsarlo.
    initMdbOn(this.pager, this.mdb);
  }

  /** Repinta y ajusta qué se ve. No persiste nada ni emite eventos. */
  _apply() {
    // En remoto, el total y la página los declara la aplicación en el propio
    // contenedor. Se releen en cada repintado: así, para actualizar el pie, la
    // app solo tiene que devolver el HTML con los atributos al día, sin llamar
    // a ningún método del theme.
    if (this.remote) {
      this.total = Number(this.root.getAttribute('data-company-data-view-total')) || 0;
      this.page = Number(this.root.getAttribute('data-company-data-view-page')) || 1;
    }

    const empty = this.isEmpty;
    const sinCoincidencias = this.isFiltered;

    // Si la tabla perdió filas, la página actual puede haber dejado de existir.
    if (this.page > this.totalPages) this.page = this.totalPages;

    // El tablero pagina por columna, no en conjunto: aquí las filas se dejan
    // todas visibles y cada columna se queda con su trozo al pintarse.
    if (this.view === 'board') this.allRows.forEach((row) => { row.hidden = false; });
    else this._paginateTable();

    if (empty || sinCoincidencias) {
      this.emptyHost.innerHTML = '';
      // Dos vacíos distintos: «todavía no hay nada» invita a crear el primero;
      // «no encontré eso» invita a cambiar la búsqueda. Ofrecer «crear» a quien
      // solo escribió mal una palabra es mandarlo por el camino equivocado.
      this.emptyHost.appendChild(sinCoincidencias ? this._noMatches() : this._empty());
    } else {
      // Se vacía al recuperar datos: ocultarlo bastaría para la vista, pero
      // dejaría el bloque en el documento.
      this.emptyHost.innerHTML = '';

      if (this.view === 'cards') this.render();
      else if (this.view === 'board') this.renderBoard();
    }

    const sinVistas = empty || sinCoincidencias;

    this.emptyHost.hidden = !sinVistas;
    this.tableHost.hidden = sinVistas || this.view !== 'table';
    this.cards.hidden = sinVistas || this.view !== 'cards';
    this.board.hidden = sinVistas || this.view !== 'board';

    // Sin datos no hay nada entre lo que elegir: el selector sobra. Se oculta
    // en vez de eliminarlo, para que vuelva solo cuando lleguen registros. Con
    // una búsqueda sin resultados SÍ se deja, porque hay datos detrás.
    if (this.switch) this.switch.hidden = empty;

    // El buscador se mantiene mientras haya datos que buscar: si desapareciera
    // al no encontrar nada, no habría forma de corregir lo escrito.
    if (this.toolbar) this.toolbar.hidden = empty;

    this._renderSortableHeaders();
    this._renderSort();
    this._renderPager();
  }

  // ---------------------------------------------------------------------------
  // Consulta al servidor
  // ---------------------------------------------------------------------------

  /**
   * Pide la página al servidor y sustituye las filas.
   *
   * **La respuesta es HTML, no JSON**: las filas las pinta la aplicación con su
   * Twig de siempre —enlaces, etiquetas de estado, botones de acción—. Si
   * viniera en JSON, el theme tendría que saber dibujar cada celda, y eso es
   * contenido del negocio, que aquí no entra.
   *
   * El total llega en la cabecera `X-Total-Count`, o en un
   * `data-company-data-view-total` dentro del propio fragmento.
   *
   * @param {Object} [options] `{ debounce: true }` para esperar al teclear.
   */
  reload(options = {}) {
    if (!this.url) return;

    if (this.timer) clearTimeout(this.timer);

    if (options.debounce) {
      this.timer = setTimeout(() => this._fetch(), this.debounce);
      return;
    }

    this._fetch();
  }

  _fetch() {
    // Se cancela la consulta anterior en vez de rechazar la nueva: al revés, lo
    // último que pidió el usuario se perdería mientras llega lo que ya no mira.
    if (this.controller) this.controller.abort();
    this.controller = typeof AbortController === 'function' ? new AbortController() : null;

    const url = new URL(this.url, window.location.href);
    url.searchParams.set(this.params.page, String(this.page));
    url.searchParams.set(this.params.limit, String(this.pageSize));
    if (this.search) url.searchParams.set(this.params.query, this.search);

    if (this.sort.index !== null) {
      const column = this._columns()[this.sort.index];
      // El nombre del campo si el `<th>` lo declara; si no, el índice. Con el
      // nombre, el backend no tiene que traducir posiciones a columnas.
      url.searchParams.set(this.params.sort, column && column.field ? column.field : this.sort.index);
      url.searchParams.set(this.params.dir, this.sort.dir);
    }

    this.root.classList.add('data-view-loading');

    fetch(url.toString(), {
      signal: this.controller ? this.controller.signal : undefined,
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        this._totalFromHeader = response.headers.get('X-Total-Count');
        return response.text();
      })
      .then((html) => {
        this.root.classList.remove('data-view-loading');
        this._replaceRows(html);

        // Se avisa de la consulta REAL, no de cada pulsación: al teclear se
        // emite `searchChanged` por letra, pero la petición es una sola.
        this.root.dispatchEvent(
          new CustomEvent('loaded.company.dataview', {
            bubbles: true,
            detail: { url: url.pathname + url.search, total: this.totalRows, id: this.id },
          })
        );
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return; // la reemplazó otra
        this.root.classList.remove('data-view-loading');
        this._showError();
      });
  }

  _replaceRows(html) {
    const body = this.table.tBodies[0];
    if (!body) return;

    // Se interpreta dentro de una tabla: un `<tr>` suelto en un `<div>` lo
    // descarta el navegador, porque solo es válido dentro de una tabla.
    const holder = document.createElement('table');
    holder.innerHTML = `<tbody>${html}</tbody>`;

    const declarado = holder.querySelector('[data-company-data-view-total]');
    const total = this._totalFromHeader || (declarado && declarado.getAttribute('data-company-data-view-total'));

    if (total !== null && total !== undefined && total !== false) {
      this.root.setAttribute('data-company-data-view-total', String(total));
      // También en memoria: el atributo solo se relee al repintar, que va en la
      // microtarea del observador. Sin esto, lo que se lea justo después —el
      // pie, el evento `loaded`— daría el total de la consulta anterior.
      this.total = Number(total) || 0;
    } else {
      // Sin total, antes se quedaba el de la consulta anterior —un cero recién
      // arrancado—, y el pie decía «0 resultados» con filas en pantalla.
      // Mentir sobre lo que se ve es peor que quedarse corto: se cuenta lo que
      // ha llegado, que al menos es verdad para esta página.
      //
      // No arregla la paginación: sin saber cuántos hay en total, el paginador
      // cree que solo existe esta página. Eso lo tiene que resolver el servidor,
      // y por eso además se avisa por consola.
      //
      // Va al atributo Y a memoria, las dos cosas. El atributo porque al
      // repintar se vuelve a leer de ahí, y si se quedara el de antes borraría
      // esta cuenta en la microtarea siguiente; en memoria porque el atributo
      // no se relee hasta ese repintado.
      const enPantalla = holder.querySelectorAll('tr').length;
      this.root.setAttribute('data-company-data-view-total', String(enPantalla));
      this.total = enPantalla;
      this._warnSinTotal();
    }
    this.root.setAttribute('data-company-data-view-page', String(this.page));

    this.errorHost.hidden = true;
    this.errorHost.innerHTML = '';

    // El observador repinta solo al cambiar el `tbody`.
    body.innerHTML = holder.tBodies[0] ? holder.tBodies[0].innerHTML : '';
  }

  /**
   * Un fallo de red que deja el listado vacío se lee como «no hay datos», y el
   * usuario da por hecho que no existe lo que buscaba. Se avisa, con reintento.
   */
  _showError() {
    this.errorHost.innerHTML = `
      <div class="error-block">
        <i class="fas fa-triangle-exclamation error-block-icon"></i>
        <p class="error-block-title">${dataViewES.loadError}</p>
        <p class="error-block-text">${dataViewES.loadErrorText}</p>
        <div class="error-block-actions">
          <button type="button" class="btn btn-outline-primary btn-sm" data-retry>
            <i class="fas fa-rotate-right me-2"></i>${dataViewES.retry}
          </button>
        </div>
      </div>
    `;

    this.errorHost.querySelector('[data-retry]').addEventListener('click', () => this.reload());

    this.errorHost.hidden = false;
    this.tableHost.hidden = true;
    this.cards.hidden = true;
    this.board.hidden = true;
    this.emptyHost.hidden = true;
    this.pager.hidden = true;
  }

  /** Caja para cuando hay datos pero la búsqueda no encuentra ninguno. */
  _noMatches() {
    const box = document.createElement('div');
    box.className = 'data-view-empty';
    box.innerHTML = `
      <i class="fas fa-magnifying-glass data-view-empty-icon"></i>
      <p class="data-view-empty-title"></p>
      <p class="data-view-empty-text"></p>
      <div class="data-view-empty-actions">
        <button type="button" class="btn btn-outline-primary btn-sm" data-clear-search></button>
      </div>
    `;

    box.querySelector('.data-view-empty-title').textContent = dataViewES.noMatches;
    box.querySelector('.data-view-empty-text').textContent = dataViewES.noMatchesText;

    const button = box.querySelector('[data-clear-search]');
    button.textContent = dataViewES.clearSearch;
    button.addEventListener('click', () => {
      if (this.searchInput) this.searchInput.value = '';
      this.setSearch('');
    });

    return box;
  }

  setView(view) {
    this.view = this._normalize(view);
    this._apply();

    if (this.switch) {
      this.switch.querySelectorAll('button[data-view]').forEach((button) => {
        const active = button.dataset.view === this.view;
        button.classList.toggle('active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    writePreference(this.id, this.view);

    this.root.dispatchEvent(
      new CustomEvent('viewChanged.company.dataview', {
        bubbles: true,
        detail: { view: this.view, id: this.id },
      })
    );
  }

  dispose() {
    if (this.observer) this.observer.disconnect();

    // Las cabeceras son de la tabla, no del theme: se devuelven como estaban.
    this.table.querySelectorAll('thead th').forEach((th) => {
      if (th.__companySort) {
        th.removeEventListener('click', th.__companySort);
        delete th.__companySort;
      }
      th.classList.remove('data-sortable', 'data-sorted');
      th.removeAttribute('aria-sort');
      const arrow = th.querySelector('.data-sort-arrow');
      if (arrow) arrow.remove();
    });

    if (this.toolbar) this.toolbar.remove();
    this.cards.remove();
    this.board.remove();
    this.emptyHost.remove();
    this.pager.remove();
    // Las filas ocultadas por la paginación vuelven a mostrarse.
    this.allRows.forEach((row) => {
      row.hidden = false;
    });
    if (this.switch) this.switch.remove();

    // Se devuelve la tabla a donde estaba y se retira la tarjeta que se le puso.
    if (this.tableHost !== this.table) {
      this.tableHost.parentNode.insertBefore(this.table, this.tableHost);
      this.tableHost.remove();
    }

    this.table.hidden = false;
    delete this.root.__companyDataView;
  }

  static getInstance(root) {
    return root.__companyDataView || null;
  }
}

/**
 * Inicializa las vistas de datos presentes en `root`.
 *
 * @param {ParentNode} [root=document] Ámbito a recorrer.
 */
export function setupDataViews(root = document, components = {}) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll('[data-company-data-view]').forEach((element) => {
    if (element.__companyDataView) return;
    if (!element.querySelector('table')) return;

    element.__companyDataView = new DataView(element, components);
  });
}

export { DataView };
export default setupDataViews;
